import { isVariantBuyable } from '@lib/pharma/buyability';
import { isVariantInStock } from '@lib/util/inventory';
import type { HttpTypes } from '@medusajs/types';

export type OptionsKeymap = Record<string, string>;

type VariantOptionLike =
  | Array<{ option_id?: string | null; value: string }>
  | null
  | undefined;

/**
 * The variant whose content/description should render before the user makes
 * an explicit selection — the first in-stock variant, falling back to the
 * first variant overall (e.g. when every variant is out of stock).
 */
export function getDefaultVariant(
  variants: HttpTypes.StoreProductVariant[] | null | undefined,
): HttpTypes.StoreProductVariant | undefined {
  const list = variants ?? [];
  if (list.length === 0) return undefined;
  return list.find((v) => isVariantInStock(v)) ?? list[0];
}

export function optionsAsKeymap(
  variantOptions: VariantOptionLike,
): OptionsKeymap | undefined {
  return variantOptions?.reduce<OptionsKeymap>((acc, varopt) => {
    if (varopt.option_id) {
      acc[varopt.option_id] = varopt.value;
    }
    return acc;
  }, {});
}

export function sameOptionsKeymap(
  a: Record<string, string | undefined> | undefined,
  b: Record<string, string | undefined> | undefined,
): boolean {
  if (!a || !b) return a === b;
  const keysA = Object.keys(a);
  return (
    keysA.length === Object.keys(b).length && keysA.every((k) => a[k] === b[k])
  );
}

/**
 * Whether a single option value can lead to a purchase:
 *   - `buyable` — at least one candidate variant is sellable at all
 *                 (classification present, not flagged not_for_sale,
 *                 questionnaire present for P/POM). `false` means no selection
 *                 will ever make this value buyable — a permanent dead end.
 *   - `inStock` — at least one *buyable* candidate variant is also in stock.
 */
export type OptionValueAvailability = {
  buyable: boolean;
  inStock: boolean;
};

type OptionForAvailability = {
  id: string;
  values?: Array<{ value: string }> | null;
};

type VariantForAvailability = {
  options?: VariantOptionLike;
  metadata?: Record<string, unknown> | null;
  manage_inventory?: boolean | null;
  allow_backorder?: boolean | null;
  inventory_quantity?: number | null;
};

/**
 * Per-option-value buyability + stock, so the variant picker can mark dead
 * sizes up front instead of making the shopper tap each one to find out it
 * can't be bought.
 *
 * For each value of each option we gather the "candidate" variants — those
 * carrying that value AND consistent with whatever the shopper has already
 * picked for the *other* options — then fold the buyability/stock predicates
 * (the same ones the CTA, JSON-LD and Google product feed use) over them.
 *
 * Single-option products (the common pharmacy pack-size case) map each value to
 * exactly one variant, so the answer is exact; multi-option products get a
 * combination-aware answer that narrows as other options are selected. A value
 * with no candidate variant reads as unbuyable (fail-safe — there's nothing to
 * sell).
 */
export function getOptionValueAvailabilities(
  options: OptionForAvailability[] | null | undefined,
  variants: VariantForAvailability[] | null | undefined,
  selectedOptions: Record<string, string | undefined>,
): Record<string, Record<string, OptionValueAvailability>> {
  const list = variants ?? [];
  const result: Record<string, Record<string, OptionValueAvailability>> = {};

  for (const option of options ?? []) {
    const byValue: Record<string, OptionValueAvailability> = {};

    for (const { value } of option.values ?? []) {
      const hasValue = (variant: VariantForAvailability) =>
        optionsAsKeymap(variant.options)?.[option.id] === value;

      const narrowedCandidates = list.filter((variant) => {
        const keymap = optionsAsKeymap(variant.options) ?? {};
        if (keymap[option.id] !== value) return false;
        // Consistent with the shopper's picks for the *other* options — an
        // unselected option (or this same option) never narrows the set.
        return Object.entries(selectedOptions).every(([optionId, selected]) => {
          if (optionId === option.id || selected == null) return true;
          return keymap[optionId] === selected;
        });
      });

      // When the shopper's picks for the *other* options rule out every
      // variant carrying this value, fall back to any variant carrying it at
      // all. Without this, two already-selected options can jointly narrow a
      // value to zero candidates even though it's buyable in a different
      // combination — marking it a permanent dead end and trapping the
      // shopper between two mutually-exclusive selections with no clickable
      // way out. Selecting such a value leaves the other option's stale pick
      // in place, but since the candidate set here has changed, that other
      // option's own values are re-evaluated against the new selection on
      // the next render, so a valid combination becomes reachable again.
      const candidates =
        narrowedCandidates.length > 0
          ? narrowedCandidates
          : list.filter(hasValue);

      byValue[value] = {
        buyable: candidates.some((v) => isVariantBuyable(v)),
        inStock: candidates.some(
          (v) => isVariantBuyable(v) && isVariantInStock(v),
        ),
      };
    }

    result[option.id] = byValue;
  }

  return result;
}
